/**
 * VenueApprovalGate — Inserted between PrivateRoute and the /venue/*
 * routes. Loads the venue-owner dashboard once (which returns a stub
 * when not approved) and decides whether to render the outlet or swap
 * in <VenuePendingApproval />.
 *
 * Mirrors VendorApprovalGate but for the venue_owner role.
 */
import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';
import venueOwnerService from '@services/venueOwnerService';
import VenuePendingApproval from '@components/venue/VenuePendingApproval';
import Loader from '@components/common/Loader';

const VenueApprovalGate = () => {
  const { user } = useAuth();
  const [state, setState] = useState({ loaded: false, status: null, venue: null });

  useEffect(() => {
    if (user?.role !== 'venue_owner') { setState({ loaded: true, status: null, venue: null }); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await venueOwnerService.getDashboard();
        const data = r?.data || {};
        const status = data.pendingApproval
          ? (data.approvalStatus || 'pending')
          : 'approved';
        if (!cancelled) setState({ loaded: true, status, venue: data.venue || null });
      } catch {
        if (!cancelled) setState({ loaded: true, status: 'awaiting_assignment', venue: null });
      }
    })();
    return () => { cancelled = true; };
  }, [user?.role]);

  if (!state.loaded) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 320 }}>
        <Loader size="lg" />
      </div>
    );
  }

  // Admin / super_admin can browse the venue portal for support; only
  // gate users actually holding the venue_owner role.
  if (user?.role !== 'venue_owner' || state.status === 'approved') return <Outlet />;

  return <VenuePendingApproval status={state.status} venue={state.venue} />;
};

export default VenueApprovalGate;
